import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Japanese mirror of the NAATI vs court-registered translator guide. */
const TITLE = "NAATI認定翻訳者とタイ裁判所登録翻訳者の違い — どちらを選ぶべきか";
const DESC =
  "NAATI認定翻訳が必要な場面、タイの裁判所・弁護士会登録翻訳者が適する場面、そして二重払いと差し戻しを避ける方法。";
const PATH = "/blog/naati-vs-court-registered-translator";
const URL = `/ja${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "NAATIとは何ですか？",
    answer:
      "オーストラリアの翻訳・通訳資格認定機関です。豪州の移民・教育・行政機関では、NAATI認定翻訳者による訳文（認定番号と署名入りの証明）が求められることが一般的です。",
  },
  {
    question: "タイの裁判所・弁護士会登録翻訳者はどの場面で使いますか？",
    answer:
      "タイ国内で使用する書類、またはタイ外務省領事局と提出先国の在外公館で認証を受ける書類に用います。タイの機関が資格を認めており、認証連鎖では標準的な選択です。",
  },
  {
    question: "両者は相互に代替できますか？",
    answer:
      "一般には代替できません。豪州の機関はNAATI認定でない訳文を受理しないことが多く、タイの機関はNAATI認定であっても国内の認証手続きを免除しません。提出先の要件に従い、必要なら両方を用意します。",
  },
  {
    question: "二重払いを避けるには？",
    answer:
      "翻訳を依頼する前に、提出先機関名、受理される翻訳者資格、追加の領事・大使館認証の要否を書面で確認してください。順序を確定してから翻訳すれば、同じ書類を二度訳す事態を避けられます。",
  },
  {
    question: "訳文には何を含める必要がありますか？",
    answer:
      "原本と完全に対応させ、印章・スタンプ・注記・手書きの追記・ページ番号まで含めます。印章文言の訳し漏れは差し戻しの代表例です。",
  },
  {
    question: "NAATI訳文にも領事認証が必要ですか？",
    answer:
      "提出先によります。豪州国内のみで使う場合はNAATI訳文で足りることが多く、第三国でも使う場合はタイ領事局と当該国在外公館の認証が別途必要になることがあります。",
  },
  {
    question: "翻訳者の資格はどう確認できますか？",
    answer:
      "NAATI翻訳者は認定番号で、タイの登録翻訳者は登録記録で確認できます。受領時に証明文・署名・連絡先が揃っているかを必ず確認してください。",
  },
];

export const Route = createFileRoute("/ja/blog/naati-vs-court-registered-translator")({
  component: JaNaatiVsCourtTranslator,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "ja" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("naati-vs-court-registered-translator", "ja") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "NAATI 認定翻訳, タイ語 NAATI 翻訳者, タイ 裁判所 登録翻訳者, タイ 書類 翻訳 認証, 豪州 移民 翻訳 要件",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "ja"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "ホーム", url: "/ja" },
              { name: "ガイド", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("naati-vs-court-registered-translator"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Translation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const CHOOSE: Array<{ case_: string; pick: string }> = [
  { case_: "豪州のビザ・移民・学歴評価", pick: "NAATI認定翻訳者" },
  { case_: "タイの裁判所・行政機関への提出", pick: "裁判所／弁護士会登録翻訳者" },
  { case_: "タイ書類を第三国の在外公館で認証", pick: "登録翻訳者＋領事局認証" },
  { case_: "豪州国内の民間機関（銀行・雇用主）", pick: "多くがNAATI訳文を受理" },
  { case_: "豪州とタイの両方で使用", pick: "それぞれの訳文を別に用意" },
];

const STEPS: string[] = [
  "提出先機関の要件を書面で入手し、受理される翻訳者資格を確認する",
  "受理言語と追加認証の要否を確認する",
  "原本または認証済み謄本を用意する",
  "該当資格の翻訳者を選び、印章文言まで含めて全文翻訳する",
  "必要に応じて領事局・在外公館の認証を受ける",
  "提出前に氏名スペル・日付・ページ数を照合する",
];

const MISTAKES: string[] = [
  "要件確認より先に翻訳し、資格不適合で再翻訳になる",
  "印章・スタンプ・注記の訳し漏れ",
  "氏名スペルがパスポートと一致していない",
  "NAATI訳文ならタイ国内の認証が不要と誤解する",
  "単純コピーを翻訳の原稿に使う",
];

function JaNaatiVsCourtTranslator() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/ja" className="text-muted-foreground hover:text-foreground">
          ホーム
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          ガイド
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="naati-vs-court-registered-translator" locale="ja" />
      <p className="answer-summary speakable-summary">
        翻訳者選びの基準は「提出先がどの資格を受理するか」だけです。豪州の機関は通常NAATI認定を要求し、
        タイ領事局・在外公館の認証を通す書類は裁判所または弁護士会の登録翻訳者が適します。要件確認を先に行えば、同じ書類に二度払う事態を避けられます。
      </p>

      <h2>1. 用途別の選び方</h2>
      <ul>
        {CHOOSE.map((x) => (
          <li key={x.case_}>
            <strong>{x.case_}：</strong> {x.pick}
          </li>
        ))}
      </ul>

      <h2>2. 推奨する進め方</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. よくある間違い</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. よくある質問</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. 参考リンクと関連サービス</h2>
      <p className="text-sm">
        参考：{" "}
        <a href="https://www.naati.com.au/" target="_blank" rel="noreferrer">
          NAATI（豪州翻訳・通訳資格認定機関）
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          タイ外務省 領事局
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/ja/naati" className="text-primary underline">
            NAATI認定翻訳サービス
          </Link>
        </li>
        <li>
          <Link to="/ja/sworn" className="text-primary underline">
            宣誓・登録翻訳者サービス
          </Link>
        </li>
        <li>
          <Link to="/ja/lgz" className="text-primary underline">
            文書認証・領事認証サービス
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        内容確認日 {PUBLISHED}。本ガイドは計画のための一般情報であり、いかなる機関の決定を示すものではありません。手続き前に提出先機関で最新要件をご確認ください。
      </p>
    </article>
  );
}
